/**
 * Auth context and hooks (DEC-141, UI_SDD §5).
 *
 * **The one structural cost of the cookie session.** The session is `HttpOnly`,
 * so JavaScript cannot read it and the route guard can no longer answer
 * synchronously the way `localStorage.getItem('tdia_token')` did. Identity comes
 * from `GET /auth/me` instead, which is a network call, which means a resolving
 * state.
 *
 * The provider lives in `AuthProvider.tsx`, not here: eslint's react-refresh
 * rule requires a module to export components or non-components, not both, and
 * this project runs eslint at zero warnings.
 */
import { createContext, useContext } from 'react'
import type { AuthClaims } from '../api/client'

export type AuthStatus = 'resolving' | 'authenticated' | 'anonymous'

export interface AuthContextValue {
  status: AuthStatus
  claims: AuthClaims | null
  /** Re-read `/auth/me`. Called after a link is redeemed. */
  refresh: () => Promise<void>
  /** End the session server-side, then hard-navigate to /login. */
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === null) {
    throw new Error('useAuth must be used inside <AuthProvider>.')
  }
  return ctx
}

/**
 * The caller's profile, or null for an operator token.
 *
 * A null profile is treated as unrestricted, unchanged from before §38: tokens
 * minted by the admin-secret back door carry no profile claim and sit at the
 * same trust tier as `TDIA_ADMIN_SECRET` itself (§30.3). Every gate this feeds
 * is a UX convenience; the backend enforces the real one on every request.
 */
export function useProfile() {
  return useAuth().claims?.profile ?? null
}
